import { useRef } from "react";
import { NSBox, NSBoxEmail, NSColumn, NSRepeater } from "namirasoft-site-react";
import { NSARouterMakerResult } from "namirasoft-account-react";
import { PermissionFullRow, PermissionInputRow, PermissionRow } from "namirasoft-access";
import { AccessLayout } from "../AccessLayout";
import { AccessProps } from "../AccessProps";
import { AccessEntities } from "../AccessEntities";
import PolicyLine from "../components/PolicyLine";
import RoleLine from "../components/RoleLine";
import PermissionPoliciesRolesTables from "../components/PermissionPoliciesRolesTables";

export interface OutgoingPermissionEditPageProps extends NSARouterMakerResult, AccessProps
{
	isEdit: boolean;
}

function OutgoingPermissionEditPage(props: OutgoingPermissionEditPageProps)
{
	const NSBoxEmail_email = useRef<NSBoxEmail>(null);
	const NSRepeater_Policy = useRef<NSRepeater<PolicyLine, string>>(null);
	const NSRepeater_Role = useRef<NSRepeater<RoleLine, string>>(null);

	return (
		<AccessLayout<PermissionRow, PermissionInputRow> {...props}
			action={{
				title: "",
				description: "Grant new permissions to specific users, allowing them customized access to your account and specifying what actions they can perform.",
				menus: {}
			}}
			pages={{
				entity: AccessEntities.outgoingPermission(props),
				edit: {
					...props,
					ui: {
						getChildrenAfter: (id) => id
							? <PermissionPoliciesRolesTables
								{...props}
								id={id}
								get={props.servers.access.permission.GetOutgoing}
							/>
							: <></>,
						tab_page_edit: {
							editor: {
								getInputRow: () =>
								{
									let to_email = NSBoxEmail_email.current?.getValue() ?? "";
									let policies: (string | null)[] = NSRepeater_Policy.current?.getValue() ?? [];
									let roles: (string | null)[] = NSRepeater_Role.current?.getValue() ?? [];
									return { to_email, policies, roles, permission_category: [], permission_field: [], permission_tag: [] }
								},
								setRow: (row) =>
								{
									let e = row as PermissionFullRow;
									let policies = e.policies.filter((item): item is string => item !== null);
									let roles = e.roles.filter((item): item is string => item !== null);
									NSBoxEmail_email.current?.setValue(row.to_email);
									NSRepeater_Policy.current?.setValue(policies);
									NSRepeater_Role.current?.setValue(roles);
								},
							},
							ui: {
								extra_tabs: [
									{
										title: "Policy",
										clicked: true,
										getContent()
										{
											return (
												<NSColumn
													style={{
														width: NSBox.width.double
													}}
												>
													<NSRepeater<PolicyLine, string>
														ref={NSRepeater_Policy}
														title="Policies"
														required={false}
														createItem={(gIndex: number, lIndex: number, ref: React.RefObject<PolicyLine>, onChange: () => void, onDelete: undefined | ((lindex: number) => void)) =>
														{
															return <PolicyLine
																ref={ref}
																{...props}
																key={`OutgoingPermissionEditPage_NSRepeater_PolicyLine_key_${gIndex}`}
																index={lIndex}
																deletable={true}
																onChanged={() => onChange()}
																onDelete={() => onDelete?.(lIndex)}
																servers={props.servers}
															/>
														}}
														isEmpty={function (_, value: string | null): boolean
														{
															return !PolicyLine.IS_DIRTY(value);
														}}
														getValue={function (item: PolicyLine, checkError: boolean): string
														{
															return item.getValue(checkError);
														}}
														setValue={function (item: PolicyLine, _: number, value: string | null): void
														{
															if (value)
																item.setValue(value);
														}}
														style={{ width: "100%" }}
														info= {{link: "https://namirasoft.com/access/console-guide/permission/#field-policy"}}
													/>
												</NSColumn>
											);
										},
									},
									{
										title: "Role",
										clicked: true,
										getContent()
										{
											return (
												<NSColumn
													style={{
														width: NSBox.width.double
													}}
												>
													<NSRepeater<RoleLine, string>
														ref={NSRepeater_Role}
														title="Roles"
														required={false}
														createItem={(gIndex: number, lIndex: number, ref: React.RefObject<RoleLine>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
														{
															return <RoleLine
																ref={ref}
																{...props}
																key={`OutgoingPermissionEditPage_NSRepeater_RoleLine_key_${gIndex}`}
																index={lIndex}
																deletable={true}
																onChanged={() => onChange()}
																onDelete={() => onDelete?.(lIndex)}
																servers={props.servers}
															/>
														}}
														isEmpty={function (_, value: string | null): boolean
														{
															return !RoleLine.IS_DIRTY(value)
														}}
														getValue={function (item: RoleLine, checkError: boolean): string
														{
															return item.getValue(checkError)
														}}
														setValue={function (item: RoleLine, _: number, value: string | null): void
														{
															if (value)
																item.setValue(value);
														}}
														style={{ width: "100%" }}
														info= {{link: "https://namirasoft.com/access/console-guide/permission/#field-role"}}
													/>
												</NSColumn>
											);
										},
									}
								]
							}
						},
					}
				}
			}}
		>
			<NSColumn>
				<NSBoxEmail
					ref={NSBoxEmail_email}
					required
					title="Email"
					style={{ width: NSBox.width.double }}
					info={{link: "https://namirasoft.com/access/console-guide/permission/#field-email"}}
				/>
			</NSColumn>
		</AccessLayout>
	);
}

export default OutgoingPermissionEditPage;